import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { uniqueOrderedCodes } from './StatementXmlPreview.utils';
import type { StatementSentenceRow } from './types';

type StatementXmlPreviewCodesAndSentencesProps = {
  rows: StatementSentenceRow[];
  sx?: SxProps<Theme>;
  hoveredCode: string | null;
  onHoveredCodeChange: (code: string | null) => void;
};

export const StatementXmlPreviewCodesAndSentences: React.FC<StatementXmlPreviewCodesAndSentencesProps> = ({
  rows,
  sx,
  hoveredCode,
  onHoveredCodeChange,
}) => {
  const uniqueCodes = uniqueOrderedCodes(rows);

  return (
    <Box sx={sx}>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
        {uniqueCodes.map((code) => (
          <Chip
            key={code}
            size="small"
            color="warning"
            label={code}
            onMouseEnter={() => onHoveredCodeChange(code)}
            onMouseLeave={() => onHoveredCodeChange(null)}
            sx={(theme) => ({
              cursor: 'pointer',
              transition: 'transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
              opacity: hoveredCode && hoveredCode !== code ? 0.55 : 1,
              transform: hoveredCode === code ? 'translateY(-1px)' : 'none',
              boxShadow: hoveredCode === code ? `0 0 0 2px ${alpha(theme.palette.warning.main, 0.35)}` : 'none',
            })}
          />
        ))}
      </Stack>

      <Box sx={{ whiteSpace: 'normal', lineHeight: 1.75 }}>
        {rows.map((row, index) => {
          const shouldHighlight = hoveredCode !== null && row.code === hoveredCode;
          return (
            <Box
              key={row.rowId}
              onMouseEnter={() => onHoveredCodeChange(row.code || null)}
              onMouseLeave={() => onHoveredCodeChange(null)}
              sx={(theme) => ({
                px: 0.2,
                py: 0,
                borderRadius: 1,
                backgroundColor: shouldHighlight ? alpha(theme.palette.warning.main, 0.16) : 'transparent',
                transition: 'background-color 120ms ease-in-out',
                display: 'inline',
                cursor: row.code ? 'pointer' : 'default',
              })}
            >
              <Typography component="span" variant="body2" color="text.primary">
                {row.text}
              </Typography>
              {index < rows.length - 1 ? ' ' : ''}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
